/**
 * Simulation Preview component
 * Requirements: 1.3, 1.4, 1.5
 */

import type { SimulationConfig, SimulationState, Position, IRenderer } from '@langton/shared';
import { GridType, Topology, SimulationEngine, createGridNavigator, deserializeState, type SerializedState } from '@langton/shared';
import { createRenderer } from '../renderers/index.js';
import { loadAvatar } from '../renderers/avatar-renderer.js';

/**
 * SimulationPreview - Preview card for a simulation in the gallery
 * 
 * Requirements:
 * - 1.3: Square canvas with live preview centered on ant
 * - 1.4: Metadata below preview (name, grid type, rule set, speed, avatar, dimensions, topology)
 * - 1.5: Click navigates to full simulation page
 */
export class SimulationPreview {
  private element: HTMLElement;
  private canvas: HTMLCanvasElement;
  private renderer: IRenderer;
  private config: SimulationConfig;
  private state: SimulationState | null = null;
  private onClick: (id: string) => void;
  private animationFrameId: number | null = null;
  private localEngine: SimulationEngine | null = null;
  private lastStepTime = 0;
  private avatarImage: HTMLImageElement | null = null;

  constructor(config: SimulationConfig, onClick: (id: string) => void) {
    this.config = config;
    this.onClick = onClick;
    this.renderer = createRenderer(config.gridType);
    this.element = this.createPreviewElement();
    this.canvas = this.element.querySelector('canvas')!;
    
    // Load avatar if configured
    if (config.avatarUrl) {
      this.loadAvatarImage(config.avatarUrl);
    }
  }

  /**
   * Load avatar image
   */
  private async loadAvatarImage(url: string): Promise<void> {
    try {
      this.avatarImage = await loadAvatar(url);
      // Update renderer's avatar
      if ('loadAvatarImage' in this.renderer) {
        await (this.renderer as any).loadAvatarImage(url);
      }
    } catch (error) {
      console.error('Failed to load avatar:', error);
    }
  }

  /**
   * Create the preview DOM element
   * Requirements: 1.3, 1.4, 1.5
   */
  private createPreviewElement(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'simulation-preview';
    container.style.cssText = `
      background-color: transparent;
      border: 2px solid #FFFFFF;
      border-radius: 12px;
      padding: 16px;
      cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s;
      position: relative;
      z-index: 1;
    `;

    // Hover effects
    container.addEventListener('mouseenter', () => {
      container.style.transform = 'scale(1.02)';
      container.style.boxShadow = '0 0 15px rgba(255, 255, 255, 0.4), 0 0 30px rgba(255, 255, 255, 0.2)';
    });
    container.addEventListener('mouseleave', () => {
      container.style.transform = 'scale(1)';
      container.style.boxShadow = 'none';
    });

    // Click handler (Req 1.5)
    container.addEventListener('click', () => {
      this.onClick(this.config.id);
    });

    // Canvas for preview (Req 1.3)
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 133;
    canvas.style.cssText = `
      width: 100%;
      aspect-ratio: 3 / 2;
      border: 2px solid #FFFFFF;
      border-radius: 8px;
      display: block;
      box-sizing: border-box;
    `;
    container.appendChild(canvas);

    // Metadata container (Req 1.4)
    const metadata = this.createMetadataElement();
    container.appendChild(metadata);

    return container;
  }

  /**
   * Create metadata display element
   * Requirements: 1.4
   */
  private createMetadataElement(): HTMLElement {
    const metadata = document.createElement('div');
    metadata.className = 'preview-metadata';
    metadata.style.cssText = `
      margin-top: 12px;
      color: #FFFFFF;
      font-family: 'Arial', sans-serif;
    `;

    // Name
    const name = document.createElement('h3');
    name.className = 'preview-name';
    name.textContent = this.config.name;
    name.style.cssText = `
      margin: 0 0 8px 0;
      font-size: 1.2rem;
      font-weight: 600;
    `;
    metadata.appendChild(name);

    // Grid info row
    const gridInfo = document.createElement('div');
    gridInfo.className = 'preview-grid-info';
    gridInfo.style.cssText = `
      display: flex;
      gap: 12px;
      font-size: 0.85rem;
      color: rgba(255, 255, 255, 0.7);
      margin-bottom: 8px;
    `;

    // Grid type
    const gridType = document.createElement('span');
    gridType.className = 'preview-grid-type';
    gridType.textContent = this.getGridTypeLabel(this.config.gridType);
    gridInfo.appendChild(gridType);

    // Dimensions
    const dimensions = document.createElement('span');
    dimensions.className = 'preview-dimensions';
    dimensions.textContent = `${this.config.width}×${this.config.height}`;
    gridInfo.appendChild(dimensions);

    // Topology
    const topology = document.createElement('span');
    topology.className = 'preview-topology';
    topology.textContent = this.getTopologyLabel(this.config.topology);
    gridInfo.appendChild(topology);

    metadata.appendChild(gridInfo);

    // Rule set with color indicators
    const ruleSet = document.createElement('div');
    ruleSet.className = 'preview-rule-set';
    ruleSet.style.cssText = `
      display: flex;
      gap: 4px;
      flex-wrap: wrap;
      margin-bottom: 8px;
    `;

    for (let i = 0; i < this.config.colors.length; i++) {
      const colorBox = document.createElement('span');
      colorBox.className = 'preview-color-indicator';
      colorBox.style.cssText = `
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        background-color: ${this.config.colors[i]};
        border-radius: 4px;
        font-size: 0.7rem;
        color: ${this.getContrastColor(this.config.colors[i])};
        font-weight: bold;
      `;
      colorBox.textContent = this.config.ruleSet[i]?.toString() || '0';
      ruleSet.appendChild(colorBox);
    }
    metadata.appendChild(ruleSet);

    // Speed and avatar row
    const bottomRow = document.createElement('div');
    bottomRow.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.85rem;
      color: rgba(255, 255, 255, 0.7);
    `;

    // Speed
    const speed = document.createElement('span');
    speed.className = 'preview-speed';
    speed.textContent = `${this.config.speed} шагов/сек`;
    bottomRow.appendChild(speed);

    // Avatar thumbnail
    if (this.config.avatarUrl) {
      const avatar = document.createElement('img');
      avatar.className = 'preview-avatar';
      avatar.src = this.config.avatarUrl;
      avatar.alt = 'Avatar';
      avatar.style.cssText = `
        width: 24px;
        height: 24px;
        border-radius: 4px;
        object-fit: cover;
      `;
      bottomRow.appendChild(avatar);
    }

    metadata.appendChild(bottomRow);

    return metadata;
  }

  /**
   * Get human-readable grid type label
   */
  private getGridTypeLabel(gridType: GridType): string {
    switch (gridType) {
      case GridType.Square: return 'Квадратная';
      case GridType.Triangular: return 'Треугольная';
      case GridType.Hexagonal: return 'Шестиугольная';
      default: return gridType;
    }
  }

  /**
   * Get human-readable topology label
   */
  private getTopologyLabel(topology: Topology): string {
    switch (topology) {
      case Topology.Torus: return 'Тор';
      case Topology.KleinBottle: return 'Бутылка Клейна';
      case Topology.ProjectivePlane: return 'Проективная плоскость';
      case Topology.Sphere: return 'Сфера';
      default: return topology;
    }
  }

  /**
   * Get contrasting text color for a background
   */
  private getContrastColor(hexColor: string): string {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  }

  /**
   * Initialize with state from server and start local simulation
   */
  async initializeWithState(serializedState: SerializedState, config: SimulationConfig): Promise<void> {
    this.config = config;
    this.state = deserializeState(serializedState);
    this.renderer = createRenderer(config.gridType);
    
    // Load render quality setting from server for preview
    try {
      const response = await fetch('/api/simulations/settings/render-quality');
      if (response.ok) {
        const data = await response.json();
        if (this.renderer.setOffscreenCellSize && data.quality) {
          // Use lower quality for previews (half of full quality)
          this.renderer.setOffscreenCellSize(Math.max(10, Math.floor(data.quality / 2)));
        }
      }
    } catch (error) {
      console.error('Failed to load render quality:', error);
    }
    
    // Load avatar
    if (config.avatarUrl && 'loadAvatarImage' in this.renderer) {
      (this.renderer as any).loadAvatarImage(config.avatarUrl);
    }
    
    // Create local simulation engine
    const navigator = createGridNavigator(
      config.gridType,
      config.topology,
      config.width,
      config.height
    );
    this.localEngine = new SimulationEngine(config, navigator);
    this.localEngine.setState(this.state);
    
    // Start animation loop
    this.startAnimation();
  }

  /**
   * Start animation loop for local simulation
   */
  private startAnimation(): void {
    if (this.animationFrameId !== null) return;
    
    const animate = (timestamp: number) => {
      if (!this.config || !this.localEngine || !this.state) {
        this.animationFrameId = requestAnimationFrame(animate);
        return;
      }

      // Calculate steps to execute based on speed (limit for preview)
      const effectiveSpeed = Math.min(this.config.speed, 30); // Cap at 30 for preview
      const stepInterval = 1000 / effectiveSpeed;
      const elapsed = timestamp - this.lastStepTime;

      if (elapsed >= stepInterval) {
        const stepsToExecute = Math.min(Math.floor(elapsed / stepInterval), 5);
        for (let i = 0; i < stepsToExecute; i++) {
          this.localEngine.step();
        }
        this.state = this.localEngine.getState();
        this.lastStepTime = timestamp - (elapsed % stepInterval);
        this.render();
      }

      this.animationFrameId = requestAnimationFrame(animate);
    };

    this.lastStepTime = performance.now();
    this.animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * Update preview with new state (from server sync)
   * Requirements: 1.3
   */
  update(state: SimulationState, config?: SimulationConfig): void {
    if (config) {
      this.config = config;
      this.renderer = createRenderer(config.gridType);
      
      // Load avatar
      if (config.avatarUrl && 'loadAvatarImage' in this.renderer) {
        (this.renderer as any).loadAvatarImage(config.avatarUrl);
      }
    }
    
    // If we have a local engine, sync state
    if (this.localEngine) {
      this.localEngine.setState(state);
    }
    this.state = state;
    
    // Start animation if not already running
    if (this.animationFrameId === null && this.localEngine === null) {
      // Initialize local engine
      const navigator = createGridNavigator(
        this.config.gridType,
        this.config.topology,
        this.config.width,
        this.config.height
      );
      this.localEngine = new SimulationEngine(this.config, navigator);
      this.localEngine.setState(state);
      this.startAnimation();
    }
    
    this.render();
  }

  /**
   * Render the preview canvas
   * Requirements: 1.3 - centered on ant position
   */
  private render(): void {
    if (!this.state) return;
    this.renderer.renderPreview(this.state, this.config, this.canvas);
  }

  /**
   * Get the DOM element
   */
  getElement(): HTMLElement {
    return this.element;
  }

  /**
   * Get the current config
   */
  getConfig(): SimulationConfig {
    return this.config;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.localEngine = null;
  }
}

/**
 * Calculate preview offset to center on ant position
 * Used for Property 9 testing
 */
export function calculatePreviewOffset(
  antPosition: Position,
  canvasWidth: number,
  canvasHeight: number,
  cellSize: number
): Position {
  const antPixelX = antPosition.x * cellSize + cellSize / 2;
  const antPixelY = antPosition.y * cellSize + cellSize / 2;
  return {
    x: canvasWidth / 2 - antPixelX,
    y: canvasHeight / 2 - antPixelY
  };
}

/**
 * Extract metadata fields from a simulation config
 * Used for Property 10 testing
 */
export function extractPreviewMetadata(config: SimulationConfig): {
  name: string;
  gridType: GridType;
  ruleSet: number[];
  colors: string[];
  speed: number;
  avatarUrl: string;
  width: number;
  height: number;
  topology: Topology;
} {
  return {
    name: config.name,
    gridType: config.gridType,
    ruleSet: config.ruleSet,
    colors: config.colors,
    speed: config.speed,
    avatarUrl: config.avatarUrl,
    width: config.width,
    height: config.height,
    topology: config.topology
  };
}
